import { useCallback, useRef, useState } from 'react'

import { datadogRum } from '@datadog/browser-rum'

import {
  getTurnstileSiteKey,
  TurnstileAction,
  TurnstileTokenLossReason,
  TurnstileWidgetHandle,
  TurnstileWidgetProps,
} from './TurnstileWidget'

/**
 * How long a submit waits for the widget to mint a token before giving up.
 * Production logs (2026-10-06) showed real drivers on mobile carriers going
 * out tokenless with the old 5s wait: a fresh page load right after the short
 * form, or the re-challenge after a resend, regularly took longer than that.
 */
export const TOKEN_WAIT_MS = 15_000

/** Why a submit is going out without a token. Sent to the API as
 *  `turnstileClientReason` so the server log can tell the cases apart. */
export type TurnstileMissReason =
  /** Script loaded but no token arrived within TOKEN_WAIT_MS, twice. */
  | 'timeout'
  /** The Turnstile script never loaded (ad blocker, offline, blocked CDN). */
  | 'script-blocked'
  /** First timeout: the widget has been made visible so the driver can
   *  complete the check. The caller should NOT send; ask them to try again. */
  | 'needs-interaction'

export interface TurnstileTokenResult {
  token?: string
  reason?: TurnstileMissReason
}

export const TURNSTILE_INTERACTION_MESSAGE =
  'Please complete the security check, then try again.'

export interface UseTurnstileTokenResult {
  /** True when a site key is configured and a token will be requested. */
  enabled: boolean
  /** True while `getToken` is waiting on the widget; show "Verifying…". */
  verifying: boolean
  /** Spread onto <TurnstileWidget>; pass `widgetRef` as its `ref`. */
  widgetProps: Pick<TurnstileWidgetProps, 'action' | 'onToken' | 'visible'>
  widgetRef: React.RefObject<TurnstileWidgetHandle>
  /** Resolves with the current token, waiting if the widget is still
   *  verifying. Resolves with a `reason` and no token when disabled is false
   *  and none arrives; `{}` when Turnstile isn't configured. */
  getToken: () => Promise<TurnstileTokenResult>
  /** Discard the used token and ask the widget for a new one. Call after
   *  every request that carried (or should have carried) a token. */
  reset: () => void
}

/**
 * Owns the Turnstile token for one form. Keeps the token in a ref rather than
 * state so a submit handler can read it synchronously, and lets a submit that
 * races the widget wait for the token instead of sending none.
 *
 * Miss handling: the first time the wait expires, the widget is switched from
 * invisible to visible and the caller is told to hold the request
 * ('needs-interaction'). If the next attempt also times out, the request goes
 * out tokenless with reason 'timeout' and the API decides (log-only vs
 * enforce). A blocked script skips the wait entirely ('script-blocked').
 */
/** How a `getToken` call resolved, for the `turnstile_token_wait` RUM event. */
export type TurnstileWaitOutcome =
  /** A token was already minted; no wait. */
  | 'ready'
  /** The token arrived during the wait. */
  | 'token'
  | TurnstileMissReason

/**
 * One event per submit that asked for a token, so the driver funnel can show
 * how long drivers sat on "Verifying your browser…" and how often the check
 * held or missed. Not emitted when Turnstile isn't configured.
 */
const trackTokenWait = (
  action: TurnstileAction,
  outcome: TurnstileWaitOutcome,
  waitMs: number
) => {
  datadogRum.addAction('turnstile_token_wait', {
    turnstile_action: action,
    outcome,
    wait_ms: waitMs,
  })
}

export const useTurnstileToken = (
  action: TurnstileAction
): UseTurnstileTokenResult => {
  const enabled = Boolean(getTurnstileSiteKey())
  const tokenRef = useRef<string | null>(null)
  const waitersRef = useRef<Array<(token: string | null) => void>>([])
  const widgetRef = useRef<TurnstileWidgetHandle>(null)
  const scriptBlockedRef = useRef(false)
  const interactionRequestedRef = useRef(false)
  const [verifying, setVerifying] = useState(false)
  const [visible, setVisible] = useState(false)

  const onToken = useCallback(
    (token: string | null, reason?: TurnstileTokenLossReason) => {
      tokenRef.current = token
      if (reason === 'script-blocked') {
        scriptBlockedRef.current = true
        // Nothing will ever arrive: release anyone waiting now.
        const waiters = waitersRef.current
        waitersRef.current = []
        waiters.forEach((resolve) => resolve(null))
        return
      }
      if (token) {
        // A token proves the widget is alive again (e.g. remounted after a
        // failed script load), so a later miss must wait, not short-circuit.
        scriptBlockedRef.current = false
        const waiters = waitersRef.current
        waitersRef.current = []
        waiters.forEach((resolve) => resolve(token))
      }
    },
    []
  )

  const getToken = useCallback(async (): Promise<TurnstileTokenResult> => {
    if (!enabled) return {}
    if (tokenRef.current) {
      trackTokenWait(action, 'ready', 0)
      return { token: tokenRef.current }
    }
    if (scriptBlockedRef.current) {
      trackTokenWait(action, 'script-blocked', 0)
      return { reason: 'script-blocked' }
    }

    const startedAt = Date.now()
    setVerifying(true)
    const token = await new Promise<string | null>((resolve) => {
      const timer = setTimeout(() => {
        waitersRef.current = waitersRef.current.filter((w) => w !== onResolve)
        resolve(null)
      }, TOKEN_WAIT_MS)
      const onResolve = (value: string | null) => {
        clearTimeout(timer)
        resolve(value)
      }
      waitersRef.current.push(onResolve)
    })
    setVerifying(false)
    const waitMs = Date.now() - startedAt

    if (token) {
      trackTokenWait(action, 'token', waitMs)
      return { token }
    }
    if (scriptBlockedRef.current) {
      trackTokenWait(action, 'script-blocked', waitMs)
      return { reason: 'script-blocked' }
    }
    if (!interactionRequestedRef.current) {
      interactionRequestedRef.current = true
      setVisible(true)
      trackTokenWait(action, 'needs-interaction', waitMs)
      return { reason: 'needs-interaction' }
    }
    trackTokenWait(action, 'timeout', waitMs)
    return { reason: 'timeout' }
  }, [action, enabled])

  const reset = useCallback(() => {
    tokenRef.current = null
    widgetRef.current?.reset()
  }, [])

  return {
    enabled,
    verifying,
    widgetProps: { action, onToken, visible },
    widgetRef,
    getToken,
    reset,
  }
}
